import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Button,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner
} from 'reactstrap'
import { createRewardPartner, getRewardsAdminErrorMessage, updateRewardPartner } from '../../../../helpers/api/rewardsAdmin'
import { Toast } from '../../../UI/toast/Toast'

const EMPTY = { name: '', slug: '', logo: '', rate: '', status: 'active' }

// "Seguros Horizonte" -> "seguros-horizonte" (alpha_dash, como pide el backend).
const slugify = (text) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)

const validate = (form, isNew) => {
  const errors = {}
  const rate = Number(String(form.rate).replace(',', '.'))
  if (!form.name.trim()) errors.name = 'Ingresa el nombre del aliado.'
  if (isNew && !/^[A-Za-z0-9_-]+$/.test(form.slug)) errors.slug = 'Solo letras, números, guiones y guiones bajos.'
  if (String(form.rate).trim() === '' || !Number.isFinite(rate) || rate < 0 || rate > 100) {
    errors.rate = 'Debe estar entre 0 y 100 %.'
  }
  return errors
}

// HO-TASK-289: alta y edición de un aliado. El slug identifica al aliado en el
// webhook, por eso solo se elige al crearlo.
const PartnerModal = ({ isOpen, partner, onClose, onSaved }) => {
  const isNew = !partner
  const [form, setForm] = useState(EMPTY)
  const [slugEdited, setSlugEdited] = useState(false)
  const [touched, setTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState(null)

  useEffect(() => {
    if (!isOpen) return
    setForm(
      partner
        ? {
          name: partner.name,
          slug: partner.slug,
          logo: partner.logo || '',
          rate: String(Math.round(Number(partner.affiliate_commission_rate) * 100 * 10000) / 10000),
          status: partner.status
        }
        : EMPTY
    )
    setSlugEdited(false)
    setTouched(false)
    setServerError(null)
  }, [isOpen, partner])

  const errors = validate(form, isNew)
  const hasErrors = Object.keys(errors).length > 0

  const update = (field) => ({ target }) =>
    setForm((current) => {
      const next = { ...current, [field]: target.value }
      if (field === 'name' && isNew && !slugEdited) next.slug = slugify(target.value)
      return next
    })

  const save = async () => {
    setTouched(true)
    if (hasErrors) return
    const body = {
      name: form.name.trim(),
      logo: form.logo.trim() || null,
      affiliate_commission_rate: Math.round((Number(String(form.rate).replace(',', '.')) / 100) * 1000000) / 1000000,
      status: form.status
    }

    setSaving(true)
    setServerError(null)
    try {
      const response = isNew
        ? await createRewardPartner({ ...body, slug: form.slug })
        : await updateRewardPartner(partner.id, body)
      Toast.fire({ type: 'success', title: isNew ? 'Aliado creado.' : 'Aliado guardado.' })
      onSaved(response)
    } catch (error) {
      setServerError(getRewardsAdminErrorMessage(error, 'No pudimos guardar el aliado.'))
    } finally {
      setSaving(false)
    }
  }

  const showError = (field) => touched && !!errors[field]

  return (
    <Modal isOpen={isOpen} toggle={saving ? undefined : onClose} centered>
      <ModalHeader toggle={saving ? undefined : onClose}>{isNew ? 'Nuevo aliado' : `Editar ${partner.name}`}</ModalHeader>
      <ModalBody>
        {serverError && <Alert color="danger" className="fs--1">{serverError}</Alert>}
        <FormGroup>
          <Label for="partner-name" className="fs--1">Nombre</Label>
          <Input id="partner-name" bsSize="sm" maxLength={150} value={form.name} invalid={showError('name')} onChange={update('name')} />
          <FormFeedback>{errors.name}</FormFeedback>
        </FormGroup>
        <FormGroup>
          <Label for="partner-slug" className="fs--1">Identificador (slug)</Label>
          <Input
            id="partner-slug"
            bsSize="sm"
            maxLength={80}
            value={form.slug}
            disabled={!isNew}
            invalid={showError('slug')}
            onChange={(event) => {
              setSlugEdited(true)
              update('slug')(event)
            }}
          />
          <FormFeedback>{errors.slug}</FormFeedback>
          <small className="text-600">
            {isNew ? 'Identifica al aliado en el webhook. No se puede cambiar después.' : 'No se puede cambiar.'}
          </small>
        </FormGroup>
        <Row form>
          <Col sm={6}>
            <FormGroup>
              <Label for="partner-rate" className="fs--1">Comisión de afiliado</Label>
              <InputGroup size="sm">
                <Input
                  id="partner-rate"
                  type="number"
                  min={0}
                  max={100}
                  step="any"
                  value={form.rate}
                  invalid={showError('rate')}
                  onChange={update('rate')}
                />
                <InputGroupAddon addonType="append">
                  <InputGroupText>%</InputGroupText>
                </InputGroupAddon>
                <FormFeedback>{errors.rate}</FormFeedback>
              </InputGroup>
            </FormGroup>
          </Col>
          <Col sm={6}>
            <FormGroup>
              <Label for="partner-status" className="fs--1">Estado</Label>
              <Input id="partner-status" type="select" bsSize="sm" value={form.status} onChange={update('status')}>
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </Input>
            </FormGroup>
          </Col>
        </Row>
        <FormGroup className="mb-0">
          <Label for="partner-logo" className="fs--1">URL del logo (opcional)</Label>
          <Input id="partner-logo" bsSize="sm" maxLength={255} value={form.logo} onChange={update('logo')} />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="falcon-default" size="sm" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button color="primary" size="sm" onClick={save} disabled={saving || (touched && hasErrors)}>
          {saving && <Spinner size="sm" className="mr-2" />}
          {isNew ? 'Crear aliado' : 'Guardar'}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

PartnerModal.propTypes = { isOpen: PropTypes.bool.isRequired, partner: PropTypes.object, onClose: PropTypes.func.isRequired, onSaved: PropTypes.func.isRequired }

export default PartnerModal
